import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";
import { Link } from "react-router-dom";
import { logout } from "../actions/session_actions";
import { changeLoading } from "../actions/loader_actions";
import { InterfaceButton } from "./ui/button/interface_button";
import { ButtonVariant, ButtonSize } from "./ui/button/types";
import {
  NavigationItem,
  SPECIAL_PATHS,
  ABOUT_ITEMS,
  PRODUCT_ITEMS,
  getNavigationItems,
  ExpandedDesktopItem,
} from "./navbar/navbarConfig";
import "../styles/css/navbar_v2.css";

interface NavBarProps {
  history: any;
  location: {
    pathname: string;
  };
}

interface RootState {
  session: {
    currentUser?: {
      id: string;
      email: string;
    } | null;
  };
}

const NavBar_v2: React.FC<NavBarProps> = ({ history, location }) => {
  const dispatch = useDispatch();

  // Redux selectors
  const loggedIn = useSelector((state: RootState) =>
    Boolean(state.session.currentUser)
  );

  // Local state - ensure all start as false
  const [mobileMenu, setMobileMenu] = useState<boolean>(false);
  const [about, setAbout] = useState<boolean>(false);
  const [products, setProducts] = useState<boolean>(false);
  const [currentlyExpandedItem, setCurrentlyExpandedItem] =
    useState<ExpandedDesktopItem>(ExpandedDesktopItem.None);

  // Determine correct logo based on current route
  const getCorrectLogoClass = useCallback((): string => {
    if (location.pathname.includes("/community-loans")) {
      return "community-loans";
    }
    return "default";
  }, [location.pathname]);

  const [logoClassName, setLogoClassName] = useState<string>(getCorrectLogoClass());

  // Update logo when route changes
  useEffect(() => {
    const newLogoClassName = getCorrectLogoClass();
    if (newLogoClassName !== logoClassName) {
      setLogoClassName(newLogoClassName);
    }
  }, [location.pathname, getCorrectLogoClass, logoClassName]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenu(false);
    setAbout(false);
    setProducts(false);
    setCurrentlyExpandedItem(ExpandedDesktopItem.None);
  }, [location.pathname]);

  const handleNavigate = useCallback((): void => {
    setAbout(false);
    setProducts(false);
    setMobileMenu(false);
    setCurrentlyExpandedItem(ExpandedDesktopItem.None);
  }, []);

  const handleSignOut = useCallback((): void => {
    dispatch(changeLoading(true));
    dispatch(logout());
    handleNavigate(); // Close mobile menu after sign out
  }, [dispatch, handleNavigate]);

  const toggleMobileMenu = useCallback((): void => {
    setMobileMenu((prev) => !prev);
  }, []);

  const toggleAbout = useCallback((): void => {
    setAbout((prev) => !prev);
  }, []);

  const toggleProducts = useCallback((): void => {
    setProducts((prev) => !prev);
  }, []);

  const toggleDesktopMenu = useCallback(
    (expandedItem: ExpandedDesktopItem): void => {
      if (expandedItem === currentlyExpandedItem) {
        setCurrentlyExpandedItem(ExpandedDesktopItem.None);
      } else {
        setCurrentlyExpandedItem(expandedItem);
      }
    },
    [currentlyExpandedItem]
  );

  // Close desktop dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Element;
      if (
        currentlyExpandedItem !== ExpandedDesktopItem.None &&
        !target.closest(".navbar-v2__nav-item")
      ) {
        setCurrentlyExpandedItem(ExpandedDesktopItem.None);
      }
    };

    if (currentlyExpandedItem !== ExpandedDesktopItem.None) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [currentlyExpandedItem]);

  const renderNavigationItem = (
    item: NavigationItem,
    key: string
  ): React.JSX.Element => {
    if (item.onClick) {
      return (
        <button
          key={key}
          onClick={item.onClick}
          className="navbar-v2__nav-link navbar-v2__nav-button"
        >
          {item.label}
        </button>
      );
    }

    if (item.href && item.external) {
      return (
        <a
          key={key}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className="navbar-v2__nav-link"
        >
          {item.label}
        </a>
      );
    }

    return (
      <Link
        key={key}
        to={item.to || "/"}
        onClick={handleNavigate}
        className="navbar-v2__nav-link"
      >
        {item.label}
      </Link>
    );
  };

  const renderDropdownItem = (
    item: NavigationItem,
    key: string
  ): React.JSX.Element => {
    if (item.href && item.external) {
      return (
        <a key={key} href={item.href} target="_blank" rel="noopener noreferrer">
          {item.label}
        </a>
      );
    }

    return (
      <Link key={key} to={item.to || "/"} onClick={handleNavigate}>
        {item.label}
      </Link>
    );
  };

  const renderDropdownItems = (items: NavigationItem[]): React.JSX.Element[] => {
    return items.map((item, index) => (
      <li key={index}>{renderDropdownItem(item, `dropdown-${index}`)}</li>
    ));
  };

  const renderDesktopNav = (): React.JSX.Element => {
    const navigationItems = getNavigationItems(loggedIn, handleSignOut);

    return (
      <>
        <li
          className={`navbar-v2__nav-item ${
            currentlyExpandedItem === ExpandedDesktopItem.About
              ? "navbar-v2__nav-item--open"
              : ""
          }`}
        >
          <button
            className="navbar-v2__nav-link navbar-v2__nav-button"
            onClick={() => toggleDesktopMenu(ExpandedDesktopItem.About)}
          >
            About
          </button>
          <div className="navbar-v2__dropdown">
            <div className="navbar-v2__dropdown-content">
              <ul>{renderDropdownItems(ABOUT_ITEMS)}</ul>
            </div>
          </div>
        </li>
        <li
          className={`navbar-v2__nav-item ${
            currentlyExpandedItem === ExpandedDesktopItem.Products
              ? "navbar-v2__nav-item--open"
              : ""
          }`}
        >
          <button
            className="navbar-v2__nav-link navbar-v2__nav-button"
            onClick={() => toggleDesktopMenu(ExpandedDesktopItem.Products)}
          >
            Products
          </button>
          <div className="navbar-v2__dropdown">
            <div className="navbar-v2__dropdown-content">
              <ul>{renderDropdownItems(PRODUCT_ITEMS)}</ul>
            </div>
          </div>
        </li>
        {navigationItems.map((item, index) => (
          <li key={index} className="navbar-v2__nav-item">
            {renderNavigationItem(item, `nav-${index}`)}
          </li>
        ))}
      </>
    );
  };

  const renderMobileNavigationItem = (
    item: NavigationItem,
    key: string
  ): React.JSX.Element => {
    if (item.onClick) {
      return (
        <button
          key={key}
          onClick={item.onClick}
          className="navbar-v2__mobile-link navbar-v2__mobile-button"
        >
          {item.label}
        </button>
      );
    }

    if (item.href && item.external) {
      return (
        <a
          key={key}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className="navbar-v2__mobile-link"
        >
          {item.label}
        </a>
      );
    }

    return (
      <Link
        key={key}
        to={item.to || "/"}
        onClick={handleNavigate}
        className="navbar-v2__mobile-link"
      >
        {item.label}
      </Link>
    );
  };

  const renderMobileDropdownItem = (
    item: NavigationItem,
    key: string
  ): React.JSX.Element => {
    if (item.href && item.external) {
      return (
        <a key={key} href={item.href} target="_blank" rel="noopener noreferrer">
          {item.label}
        </a>
      );
    }

    return (
      <Link key={key} to={item.to || "/"} onClick={handleNavigate}>
        {item.label}
      </Link>
    );
  };

  const renderMobileDropdownItems = (
    items: NavigationItem[]
  ): React.JSX.Element[] => {
    return items.map((item, index) => (
      <li key={index}>
        {renderMobileDropdownItem(item, `mobile-dropdown-${index}`)}
      </li>
    ));
  };

  const renderMobileNav = (): React.JSX.Element => {
    const navigationItems = getNavigationItems(loggedIn, handleSignOut);

    return (
      <nav
        className={`navbar-v2__mobile-nav ${
          mobileMenu ? "navbar-v2__mobile-nav--open" : ""
        }`}
      >
        <div className="navbar-v2__mobile-content">
          <button
            onClick={toggleAbout}
            className={`navbar-v2__mobile-link navbar-v2__mobile-button ${
              about ? "navbar-v2__mobile-link--active" : ""
            }`}
          >
            About
          </button>
          {about && (
            <div className="navbar-v2__mobile-submenu">
              <ul>{renderMobileDropdownItems(ABOUT_ITEMS)}</ul>
            </div>
          )}
          <button
            onClick={toggleProducts}
            className={`navbar-v2__mobile-link navbar-v2__mobile-button ${
              products ? "navbar-v2__mobile-link--active" : ""
            }`}
          >
            Products
          </button>
          {products && (
            <div className="navbar-v2__mobile-submenu">
              <ul>{renderMobileDropdownItems(PRODUCT_ITEMS)}</ul>
            </div>
          )}
          {navigationItems.map((item, index) =>
            renderMobileNavigationItem(item, `mobile-nav-${index}`)
          )}
        </div>
      </nav>
    );
  };

  // Hide Apply button during application flow and on specific pages
  const isInApplicationFlow: boolean = SPECIAL_PATHS.application.some(
    (path) =>
      location.pathname.startsWith(path) || location.pathname.includes(path)
  );

  const isOnSpecialPage: boolean = SPECIAL_PATHS.specialPages.some((path) =>
    location.pathname.includes(path)
  );

  const showApplyButton: boolean = !isInApplicationFlow && !isOnSpecialPage;

  return (
    <header className="navbar-v2">
      <div className="navbar-v2__container">
        <div className="navbar-v2__content">
          {/* Mobile menu toggle */}
          <button
            className="navbar-v2__mobile-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle navigation menu"
          >
            <span
              className={`navbar-v2__hamburger ${
                mobileMenu ? "navbar-v2__hamburger--active" : ""
              }`}
            >
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>

          {/* Logo */}
          <Link to="/" className="navbar-v2__logo-link">
            <img alt="Fig Loans" className={`navbar-v2__logo ${logoClassName}`} />
          </Link>

          {/* Mobile Apply Button */}
          {showApplyButton && (
            <div className="navbar-v2__mobile-apply">
              <InterfaceButton
                to="/app"
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SMALL}
                className="navbar-v2__apply-button"
                noUnderline={true}
                onClick={() => null}
              >
                Apply
              </InterfaceButton>
            </div>
          )}

          {/* Desktop Navigation */}
          <nav className="navbar-v2__desktop-nav">
            <ul className="navbar-v2__nav-list">
              {renderDesktopNav()}
              {showApplyButton && (
                <li className="navbar-v2__nav-item navbar-v2__nav-item--cta">
                  <InterfaceButton
                    to="/app"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.MEDIUM}
                    className="navbar-v2__apply-button"
                    noUnderline={true}
                    onClick={() => null}
                  >
                    Apply
                  </InterfaceButton>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </div>

      {/* Mobile Navigation */}
      {renderMobileNav()}

      {/* Backdrop for mobile menu */}
      {mobileMenu && (
        <div
          className="navbar-v2__backdrop"
          onClick={() => setMobileMenu(false)}
        />
      )}
    </header>
  );
};

export default withRouter(NavBar_v2);
